import type { CanvasItem } from './types';
import { tallyChairTypes, type ChairTypeOption } from './chairTypes';

interface ChairTypesPanelProps {
  items: readonly CanvasItem[];
  options: readonly ChairTypeOption[];
}

/**
 * The plan's chairs by type — what to order from the rental company. Set a
 * table's, row's or block's chair type in its properties; the types
 * themselves are the office's, in Settings → App Settings → Chair Types.
 */
export function ChairTypesPanel({ items, options }: ChairTypesPanelProps) {
  const { rows, totalChairs } = tallyChairTypes(items, options);
  const shown = rows.filter((r) => r.items > 0 || (!r.unknown && r.value !== null));
  return (
    <div className="space-y-2 text-xs" data-testid="chair-types-panel">
      <p className="text-gray-500">
        Chairs on this plan by type. Set a table&apos;s chair type in its properties.
      </p>
      {shown.length === 0 ? (
        <p className="text-gray-400" data-testid="chair-types-empty">
          No tables or chair rows on the plan yet.
        </p>
      ) : (
        <table className="w-full">
          <thead>
            <tr className="text-gray-400 text-left">
              <th className="font-normal py-1">Type</th>
              <th className="font-normal py-1 text-right">Tables</th>
              <th className="font-normal py-1 text-right">Chairs</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => (
              <tr
                key={r.value ?? '__none__'}
                className={`border-t border-gray-100 ${r.items === 0 ? 'text-gray-400' : 'text-gray-700'}`}
                data-testid={`chair-type-row-${r.value ?? 'none'}`}
              >
                <td className="py-1">
                  {r.label}
                  {r.unknown && (
                    <span className="block text-[10px] text-amber-600">No longer in Settings</span>
                  )}
                </td>
                <td className="py-1 text-right">{r.items}</td>
                <td className="py-1 text-right font-medium">{r.chairs}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-gray-300 font-semibold text-gray-900">
              <td className="py-1">Total</td>
              <td />
              <td className="py-1 text-right" data-testid="chair-types-total">
                {totalChairs}
              </td>
            </tr>
          </tfoot>
        </table>
      )}
    </div>
  );
}
